import { Injectable } from '@angular/core';
import { ConfigService } from './config.service';

declare const turnstile: any;

@Injectable({
  providedIn: 'root'
})
export class TurnstileService {
  private siteKey = '';
  private isLoaded = false;
  private isConfigLoaded = false;

  constructor(private configService: ConfigService) {}

  private async initializeConfig(): Promise<void> {
    if (this.isConfigLoaded) return;

    const config = this.configService.getCurrentConfig();
    if (config?.turnstile?.enabled && config.turnstile.siteKey) {
      this.siteKey = config.turnstile.siteKey;
      this.isConfigLoaded = true;
    }
  }

  async isEnabled(): Promise<boolean> {
    await this.initializeConfig();
    return this.isConfigLoaded && this.siteKey.length > 0;
  }

  async loadScript(): Promise<void> {
    if (this.isLoaded) return;
    await this.initializeConfig();

    if (!this.siteKey) {
      throw new Error('Turnstile site key not configured');
    }

    return new Promise((resolve, reject) => {
      if (typeof turnstile !== 'undefined') {
        this.isLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = () => {
        this.isLoaded = true;
        resolve();
      };
      script.onerror = () => reject(new Error('Failed to load Turnstile script'));
      document.head.appendChild(script);
    });
  }

  async renderWidget(containerId: string): Promise<string> {
    if (!this.isLoaded) {
      await this.loadScript();
    }

    // Check that the container element exists in DOM
    const container = document.getElementById(containerId);
    if (!container) {
      console.warn(`Turnstile container #${containerId} not found in DOM, skipping`);
      return 'TURNSTILE_DISABLED_PLACEHOLDER';
    }

    return new Promise((resolve, reject) => {
      try {
        const widgetId = turnstile.render(`#${containerId}`, {
          sitekey: this.siteKey,
          callback: (token: string) => resolve(token),
          'error-callback': () => reject(new Error('Turnstile challenge failed')),
          'expired-callback': () => reject(new Error('Turnstile token expired')),
          theme: 'auto',
          size: 'flexible',
        });
        // Store widgetId for potential reset
        (window as any).__turnstileWidgetId = widgetId;
      } catch (error) {
        reject(error);
      }
    });
  }

  async getToken(containerId: string): Promise<string> {
    const enabled = await this.isEnabled();
    if (!enabled) {
      return 'TURNSTILE_DISABLED_PLACEHOLDER';
    }
    return this.renderWidget(containerId);
  }

  resetWidget(): void {
    try {
      const widgetId = (window as any).__turnstileWidgetId;
      if (widgetId !== undefined && typeof turnstile !== 'undefined') {
        turnstile.reset(widgetId);
      }
    } catch {
      // Ignore reset errors
    }
  }

  removeWidget(): void {
    try {
      const widgetId = (window as any).__turnstileWidgetId;
      if (widgetId !== undefined && typeof turnstile !== 'undefined') {
        turnstile.remove(widgetId);
        delete (window as any).__turnstileWidgetId;
      }
    } catch {
      // Ignore remove errors
    }
  }
}
